import { useTranslation } from 'react-i18next';
import { currentLocale } from '../../i18n';
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import useAdminPermissions from '../../hooks/useAdminPermissions';
import { API_URL } from '../../utils/api';
import { fetchAvatarsBulk } from '../../utils/avatars';
import { fetchCountriesBulk } from '../../utils/countries';
import { getMapImageUrl, getRatingClass, formatWeaponName, getWeaponIconUrl } from '../../utils/formatters';
import { getUniversalEmbedUrl, formatHighlightType } from '../highlights/helpers';
import SteamAvatar from '../common/SteamAvatar';
import SteamFlag from '../common/SteamFlag';
import TeamLogo from '../leagues/TeamLogo';
import './MatchDetail.css';
import '../highlights/Highlights.css';
import DemoDownloadButton from '../common/DemoDownloadButton';
import MatchDemosButton from '../common/MatchDemosButton';
import RatingInfo from '../common/RatingInfo';
import HighlightStats from '../highlights/HighlightStats';
import HighlightPreview from '../highlights/HighlightPreview';
import MatchDrop from './MatchDrop';
import MatchPrediction from './MatchPrediction';
import WinProbability from '../live/WinProbability';
import MapControlChart from './MapControlChart';
import MatchComments from './MatchComments';
import StreamControl from './StreamControl';
import RankRevealModal from '../elo/RankRevealModal';

const MatchDetail = ({ matchid, isLive, onBack, onSelectPlayer, onSelectH2H }) => {
  const { t } = useTranslation();
  const { user, getAuthHeaders } = useAuth();
  const [matchData, setMatchData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [compareFrom, setCompareFrom] = useState(null);
  const [dropData, setDropData] = useState(null);
  const [highlights, setHighlights] = useState([]);
  const [weaponStatsData, setWeaponStatsData] = useState(null);
  const [advancedMatchData, setAdvancedMatchData] = useState(null);
  const [showWeapons, setShowWeapons] = useState(false);
  const [selectedWeapon, setSelectedWeapon] = useState(null);
  const [advancedTab, setAdvancedTab] = useState('weapons');
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [showRankReveal, setShowRankReveal] = useState(false);
  const [activeMap, setActiveMap] = useState('total');
  const [recapModalOpen, setRecapModalOpen] = useState(false);
  const [recapInstruction, setRecapInstruction] = useState('');
  const [recapRegenerating, setRecapRegenerating] = useState(false);
  const [recapError, setRecapError] = useState(null);
  const [winProbData, setWinProbData] = useState(null);

  // Historisk win probability-kurva (persisterad av live_manager vid
  // map/series end). Saknas för matcher spelade före featuren — sektionen
  // döljs då. Live-matcher följs i stället i realtid på /live-hubben.
  useEffect(() => {
    let cancelled = false;
    fetch(`${API_URL}/api/matches/${matchid}/win-probability`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!cancelled && data?.history?.length >= 2) setWinProbData(data);
      })
      .catch(() => {
        // tyst — kurvan är icke-kritisk för matchsidan
      });
    return () => { cancelled = true; };
  }, [matchid]);
  const { isOwner, isModerator, hasCommand } = useAdminPermissions();
  const canRegenerateRecap = isOwner || isModerator;
  const canRegenerateComment = hasCommand('highlight.regenerate-comment');

  // Highlight-comment regenerate modal. `commentTarget` = the highlight being
  // re-captioned (null = closed); mirrors the AI-recap regenerate flow.
  const [commentTarget, setCommentTarget] = useState(null);
  const [commentInstruction, setCommentInstruction] = useState('');
  const [commentRegenerating, setCommentRegenerating] = useState(false);
  const [commentError, setCommentError] = useState(null);

  const regenerateRecap = useCallback(async () => {
    setRecapRegenerating(true);
    setRecapError(null);
    try {
      const res = await fetch(`${API_URL}/api/web/matches/${matchid}/regenerate-recap`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ custom_instruction: recapInstruction.trim() || null }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.detail?.error || data?.error || `Server returned ${res.status}`);
      }
      setMatchData((prev) => prev ? { ...prev, ai_recap: data.ai_recap } : prev);
      setRecapInstruction('');
      setRecapModalOpen(false);
    } catch (e) {
      setRecapError(e.message || t('matchDetail.unknownError'));
    } finally {
      setRecapRegenerating(false);
    }
  }, [matchid, recapInstruction, getAuthHeaders]);

  const regenerateComment = useCallback(async () => {
    if (!commentTarget) return;
    setCommentRegenerating(true);
    setCommentError(null);
    try {
      const res = await fetch(
        `${API_URL}/api/web/highlights/${commentTarget.id}/regenerate-comment`,
        {
          method: 'POST',
          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ custom_instruction: commentInstruction.trim() || null }),
        },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.detail?.error || data?.error || `Server returned ${res.status}`);
      }
      setHighlights((prev) =>
        prev.map((h) => (h.id === data.id ? { ...h, ai_comment: data.ai_comment } : h)),
      );
      setCommentInstruction('');
      setCommentTarget(null);
    } catch (e) {
      setCommentError(e.message || t('matchDetail.unknownError'));
    } finally {
      setCommentRegenerating(false);
    }
  }, [commentTarget, commentInstruction, getAuthHeaders]);

  const handleDropData = useCallback((data) => setDropData(data), []);

  useEffect(() => {
    fetch(`${API_URL}/api/match/${matchid}`)
      .then(res => res.json())
      .then(async (data) => {
        const allPlayers = [...(data.team1_players || []), ...(data.team2_players || [])];
        const steamids = allPlayers.map(p => p.steamid64);
        if (data.mvp) steamids.push(data.mvp.steamid64);
        await Promise.all([fetchAvatarsBulk(steamids), fetchCountriesBulk(steamids)]);
        setMatchData(data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [matchid]);

  useEffect(() => {
    fetch(`${API_URL}/api/highlights/match/${matchid}`)
      .then(res => {
        if (res.status === 404 || res.status === 204) return { highlights: [] };
        if (!res.ok) throw new Error('Failed');
        return res.json();
      })
      .then(data => setHighlights(data.highlights || []))
      .catch(() => setHighlights([]));
  }, [matchid]);

  // Check if current user just got ranked in this match
  const newlyRankedPlayer = useMemo(() => {
    if (!matchData || !user?.steam_id) return null;
    const allPlayers = [...(matchData.team1_players || []), ...(matchData.team2_players || [])];
    return allPlayers.find(p => String(p.steamid64) === String(user.steam_id) && p.newly_ranked);
  }, [matchData, user]);

  useEffect(() => {
    if (!newlyRankedPlayer) return;
    const key = `rank_reveal_${matchid}_${user.steam_id}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, '1');
    setShowRankReveal(true);
  }, [newlyRankedPlayer, matchid, user]);

  const { completedHighlights, pendingHighlights } = useMemo(() => {
    const completed = highlights.filter(h => h.render_status === 'completed' && h.video_url);
    const pending = highlights.filter(h => h.render_status !== 'completed' || !h.video_url);
    return { completedHighlights: completed, pendingHighlights: pending };
  }, [highlights]);

  const matchFacts = useMemo(() => {
    if (!matchData) return [];
    const { team1_players, team2_players, mvp } = matchData;
    const allPlayers = [...(team1_players || []), ...(team2_players || [])];
    if (allPlayers.length === 0) return [];

    const facts = [];

    if (mvp) {
      facts.push({ icon: '⭐', text: t('matchDetail.factMvp', { name: mvp.name }), type: 'mvp' });
    }

    const topKiller = [...allPlayers].sort((a, b) => b.kills - a.kills)[0];
    if (topKiller) {
      facts.push({ icon: '🔫', text: t('matchDetail.factTopKills', { name: topKiller.name }), value: topKiller.kills, type: 'kills' });
    }

    const bestKD = [...allPlayers].sort((a, b) => parseFloat(b.kd) - parseFloat(a.kd))[0];
    if (bestKD) {
      facts.push({ icon: '📊', text: t('matchDetail.factBestKd', { name: bestKD.name }), value: bestKD.kd, type: 'kd' });
    }

    const bestRating = [...allPlayers].sort((a, b) => parseFloat(b.rating) - parseFloat(a.rating))[0];
    if (bestRating) {
      facts.push({ icon: '🏅', text: t('matchDetail.factBestRating', { name: bestRating.name }), value: bestRating.rating, type: 'rating' });
    }

    const withElo = allPlayers.filter(p => p.elo_change !== null && p.elo_change !== undefined && p.is_ranked !== false);
    if (withElo.length > 0) {
      const biggestGain = [...withElo].sort((a, b) => b.elo_change - a.elo_change)[0];
      if (biggestGain && biggestGain.elo_change > 0) {
        facts.push({ icon: '📈', text: t('matchDetail.factMostElo', { name: biggestGain.name }), value: `+${biggestGain.elo_change}`, type: 'elo-up' });
      }
      const biggestLoss = [...withElo].sort((a, b) => a.elo_change - b.elo_change)[0];
      if (biggestLoss && biggestLoss.elo_change < 0) {
        facts.push({ icon: '📉', text: t('matchDetail.factLostElo', { name: biggestLoss.name }), value: biggestLoss.elo_change, type: 'elo-down' });
      }
    }

    for (const h of highlights) {
      if (h.highlight_type === 'ace') {
        facts.push({ icon: '🏆', text: t('matchDetail.factAce', { name: h.player_name }), type: 'ace' });
      } else if (h.highlight_type?.startsWith('clutch_')) {
        const clutchType = h.highlight_type.replace('clutch_', '');
        facts.push({ icon: '🔥', text: t('matchDetail.factClutch', { name: h.player_name, clutch: clutchType }), type: 'clutch' });
      }
    }

    if (dropData && dropData.is_resolved) {
      const prizeName = dropData.prize?.name || 'Skin';
      facts.push({ icon: '🎁', text: t('matchDetail.factDrop', { name: dropData.winner_name, prize: prizeName }), type: 'drop' });
    }

    return facts;
  }, [matchData, highlights, dropData]);

  if (loading) return <div className="loading-screen"><div className="loader"></div></div>;
  if (!matchData) return <div className="error-screen">{t('matchDetail.notFound')}</div>;

  const { match, maps, team1_players, team2_players, mvp, players_by_map } = matchData;
  const isFinished = !isLive;
  const team1Won = match.team1_score > match.team2_score;
  const mapImageUrl = maps && maps.length > 0 ? getMapImageUrl(maps[0].map_image_url) : null;

  const seriesType = (match.series_type || 'BO1').toUpperCase();
  const isSeries = seriesType !== 'BO1' && Array.isArray(maps) && maps.length > 1;

  // Choose scoreboard data source based on activeMap tab.
  let viewT1Players = team1_players;
  let viewT2Players = team2_players;
  let viewT1Score = match.team1_score;
  let viewT2Score = match.team2_score;
  if (activeMap !== 'total' && players_by_map && players_by_map[activeMap]) {
    const pm = players_by_map[activeMap];
    viewT1Players = pm.team1_players || [];
    viewT2Players = pm.team2_players || [];
    viewT1Score = pm.team1_score;
    viewT2Score = pm.team2_score;
  }

  // Scoreboard winner reflects the active tab (per-map for BO3/BO5, series for total).
  // Match header keeps `team1Won` (series-level) untouched.
  const currentTeam1Won = !isLive && viewT1Score > viewT2Score;
  const currentTeam2Won = !isLive && viewT2Score > viewT1Score;

  const handleCompareClick = (steamid64, team, e) => {
    e.stopPropagation();
    if (compareFrom && compareFrom.steamid64 === steamid64) {
      setCompareFrom(null);
    } else {
      setCompareFrom({ steamid64, team });
    }
  };

  const handleCompareTarget = (steamid64, e) => {
    e.stopPropagation();
    if (compareFrom && onSelectH2H) {
      onSelectH2H(compareFrom.steamid64, steamid64);
      setCompareFrom(null);
    }
  };

  const TeamPlayerRow = ({ player, onPlayerClick, team }) => {
    const isSelected = compareFrom && compareFrom.steamid64 === player.steamid64;
    const isTarget = compareFrom && compareFrom.team !== team;

    return (
      <div
        className={`team-player-row clickable ${isSelected ? 'compare-selected' : ''} ${isTarget ? 'compare-target' : ''} ${player.is_leaver ? 'is-leaver' : ''} ${player.is_sub ? 'is-sub' : ''}`}
        onClick={(e) => {
          if (isTarget) {
            handleCompareTarget(player.steamid64, e);
          } else if (onPlayerClick) {
            onPlayerClick(player.steamid64);
          }
        }}
      >
        <div className="player-info">
          <SteamAvatar steamid64={player.steamid64} name={player.name} size="small" />
          <SteamFlag steamid64={player.steamid64} className="player-row-flag" />
          <span className="player-name">{player.name}</span>
          {player.is_sub && (
            <span className="player-sub-badge" title={t('openSlots.subBadge')}>
              ⬆ {t('openSlots.subBadge')}
            </span>
          )}
          {player.is_leaver && (
            <span className="player-leaver-badge" title={t('openSlots.leaverLabel')}>
              {t('openSlots.leaverLabel')}
            </span>
          )}
          {onSelectH2H && (
            <button
              className={`compare-btn ${isSelected ? 'active' : ''}`}
              onClick={(e) => handleCompareClick(player.steamid64, team, e)}
              title={t('matchDetail.compareOpponent')}
            >
              ⚔️
            </button>
          )}
        </div>
        <span className="stat">{player.kills}/{player.deaths}/{player.assists}</span>
        <span className="stat">{player.adr}</span>
        <span className="stat">{player.hs_percent}%</span>
        <span className={`stat rating ${getRatingClass(player.rating)}`}>{player.rating}</span>
        <span className={`stat diff ${player.elo_change !== null && player.elo_change !== undefined ? (player.elo_change >= 0 ? 'positive' : 'negative') : ''}`}>
          {player.elo_change !== null && player.elo_change !== undefined
            ? `${player.elo_change >= 0 ? '+' : ''}${player.elo_change}`
            : '-'}
        </span>
      </div>
    );
  };

  return (
    <div className="match-detail">
      <button className="back-button" onClick={onBack}>← {t('profile.back')}</button>

      {/* === DROP BANNER (top, before match content) === */}
      <MatchDrop matchId={matchid} onDropData={handleDropData} />

      {/* === MATCHTIPS (odds, eget tips, fördelning, facit) === */}
      <MatchPrediction matchId={matchid} />

      {/* === WIN PROBABILITY — historisk kurva (persisterad vid matchslut).
          Baren döljs: utfallet är känt, kurvan är berättelsen. === */}
      {!isLive && winProbData && (
        <WinProbability
          hideBar
          probability={null}
          history={winProbData.history}
          team1Name={winProbData.team1_name || match.team1_name}
          team2Name={winProbData.team2_name || match.team2_name}
        />
      )}

      <div
        className={`match-header ${mapImageUrl ? 'has-map-bg' : ''}`}
        style={mapImageUrl ? { '--map-bg': `url(${mapImageUrl})` } : {}}
      >
        <div className="match-header-overlay">
          <div className="match-info-top">
            {!isFinished && (
              <div className="match-detail-live-badge">
                <span className="match-detail-live-dot"></span>
                LIVE
              </div>
            )}
            {!isFinished && isOwner && (
              <StreamControl matchId={matchid} isOwner={isOwner} />
            )}
            {maps.length > 0 && (
              <div className="map-badges">
                {maps.map((m, idx) => (
                  <span key={idx} className="map-badge">{m.mapname || m.map_tag}</span>
                ))}
              </div>
            )}
            <div className="match-header-actions">
              <MatchDemosButton
                mapDemos={matchData.map_demos}
                fallbackUrl={matchData.demo_url}
                hasDemo={matchData.has_demo}
                size="medium"
              />
              <RatingInfo />
            </div>
            <span className="match-date-full">
              {new Date(match.start_time).toLocaleString(currentLocale())}
            </span>
            {matchData.tournament_info && (
              <a
                href={`/tournaments/${matchData.tournament_info.tournament_id}`}
                className="tournament-badge"
                style={{
                  display: 'inline-block',
                  background: '#FF9800',
                  color: '#000',
                  padding: '2px 10px',
                  borderRadius: 4,
                  fontSize: '0.8em',
                  fontWeight: 'bold',
                  textDecoration: 'none',
                  marginTop: 4,
                }}
                title={matchData.tournament_info.tournament_name}
              >
                🏆 {matchData.tournament_info.tournament_name} — {matchData.tournament_info.round_name}
              </a>
            )}
            {matchData.league_info && (
              <a
                href={`/leagues/${matchData.league_info.league_id}`}
                className="match-detail-league-badge"
                title={matchData.league_info.league_name}
              >
                🏅 {matchData.league_info.league_name} — {matchData.league_info.division_name}
              </a>
            )}
          </div>

          <div className="match-score-header">
            <div className={`team-header ${isFinished && team1Won ? 'winner' : ''}`}>
              {matchData.league_info?.home_logo_url && (
                <TeamLogo logoUrl={matchData.league_info.home_logo_url} name={match.team1_name} className="team-logo-md" />
              )}
              <span className="team-name-large">{match.team1_name}</span>
              {isFinished && team1Won && <span className="trophy">🏆</span>}
            </div>
            <div className="score-display">
              <span className={`score ${isFinished && team1Won ? 'winner-score' : ''}`}>{match.team1_score}</span>
              <span className="score-separator">:</span>
              <span className={`score ${isFinished && !team1Won ? 'winner-score' : ''}`}>{match.team2_score}</span>
            </div>
            <div className={`team-header ${isFinished && !team1Won ? 'winner' : ''}`}>
              {isFinished && !team1Won && <span className="trophy">🏆</span>}
              <span className="team-name-large">{match.team2_name}</span>
              {matchData.league_info?.away_logo_url && (
                <TeamLogo logoUrl={matchData.league_info.away_logo_url} name={match.team2_name} className="team-logo-md" />
              )}
            </div>
          </div>

          {maps.length > 1 && (
            <div className="map-scores">
              {maps.map((map, idx) => (
                <div key={idx} className="map-score-item">
                  <span className="map-name-small">{map.mapname}</span>
                  <span className="map-result">{map.team1_score} - {map.team2_score}</span>
                </div>
              ))}
            </div>
          )}

          {matchData.prediction && (
            <div className="match-prediction">
              <span className="prediction-label">{t('matchDetail.eloPrediction')}</span>
              <div className="prediction-bar">
                <div
                  className={`prediction-fill team1 ${team1Won ? 'correct' : ''}`}
                  style={{ width: `${matchData.prediction.team1_win_pct}%` }}
                >
                  {matchData.prediction.team1_win_pct}%
                </div>
                <div
                  className={`prediction-fill team2 ${!team1Won ? 'correct' : ''}`}
                  style={{ width: `${matchData.prediction.team2_win_pct}%` }}
                >
                  {matchData.prediction.team2_win_pct}%
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* === 3-COLUMN INFO GRID: Matchfakta | MVP | AI Recap === */}
      {isFinished && (matchFacts.length > 0 || mvp || matchData.ai_recap) && (
        <div className={`match-info-grid ${matchData.ai_recap ? 'three-col' : ''}`}>
          <div className="info-card match-facts lift-card notched-card tactical-grid">
            <div className="info-card-header">
              <span className="info-card-icon">⭐</span>
              <h3 className="section-heading">{t('matchDetail.matchFacts')}</h3>
            </div>
            <div className="match-facts-list">
              {matchFacts.map((fact, idx) => (
                <div key={idx} className={`match-fact-item ${fact.type}`}>
                  <span className="match-fact-icon">{fact.icon}</span>
                  <span className="match-fact-text">{fact.text}</span>
                  {fact.value !== undefined && (
                    <span className="match-fact-value">{fact.value}</span>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="info-card mvp-card-column lift-card notched-card">
            <div className="info-card-header">
              <span className="info-card-icon">👤</span>
              <h3 className="section-heading">{t('matchDetail.matchPlayers')}</h3>
            </div>
            {mvp ? (
              <div className="mvp-card" onClick={() => onSelectPlayer && onSelectPlayer(mvp.steamid64)}>
                <SteamAvatar steamid64={mvp.steamid64} name={mvp.name} size="large" />
                <div className="mvp-card-info">
                  <div className="mvp-card-name">
                    <SteamFlag steamid64={mvp.steamid64} className="mvp-card-flag" />
                    {mvp.name}
                  </div>
                  <div className="mvp-card-stats">
                    <div className="mvp-card-stat">
                      <span className="mvp-card-stat-label">K/D</span>
                      <span className="mvp-card-stat-value">{mvp.kills && mvp.deaths ? (mvp.kills / Math.max(mvp.deaths, 1)).toFixed(2) : '-'}</span>
                    </div>
                    <div className="mvp-card-stat">
                      <span className="mvp-card-stat-label">ADR</span>
                      <span className="mvp-card-stat-value">{mvp.adr}</span>
                    </div>
                    <div className="mvp-card-stat">
                      <span className="mvp-card-stat-label">Rating</span>
                      <span className={`mvp-card-stat-value ${getRatingClass(mvp.rating)}`}>{mvp.rating}</span>
                    </div>
                  </div>
                  {mvp.kast_percent && (
                    <div className="mvp-card-kast">{mvp.kast_percent.toFixed(1)}% KAST</div>
                  )}
                </div>
              </div>
            ) : (
              <div className="mvp-card-empty">{t('matchDetail.noMvp')}</div>
            )}
          </div>

          {matchData.ai_recap && (
            <div className="info-card ai-recap-card lift-card notched-card">
              <div className="info-card-header">
                <span className="info-card-icon">🤖</span>
                <h3 className="section-heading">AI Recap</h3>
                {canRegenerateRecap && (
                  <button
                    type="button"
                    className="ai-recap-regen-btn"
                    onClick={() => { setRecapInstruction(''); setRecapError(null); setRecapModalOpen(true); }}
                    title={t('matchDetail.regenRecapAdmin')}
                  >
                    🔄
                  </button>
                )}
              </div>
              <p className="ai-recap-text">{matchData.ai_recap}</p>
            </div>
          )}
        </div>
      )}

      {/* === MAP TAB SWITCHER (only for BO3/BO5) === */}
      {isSeries && (
        <div className="match-map-tabs">
          <button
            className={`match-map-tab ${activeMap === 'total' ? 'active' : ''}`}
            onClick={() => setActiveMap('total')}
          >
            Totalt
          </button>
          {maps.map((m, idx) => (
            <button
              key={idx}
              className={`match-map-tab ${String(activeMap) === String(idx) ? 'active' : ''}`}
              onClick={() => setActiveMap(String(idx))}
            >
              <span className="match-map-tab-num">{t('matchDetail.mapTab', { num: idx + 1 })}</span>
              <span className="match-map-tab-name">{m.mapname}</span>
              <span className="match-map-tab-score">{m.team1_score}-{m.team2_score}</span>
            </button>
          ))}
        </div>
      )}

      {/* === SIDE-BY-SIDE SCOREBOARDS === */}
      <div className={`scoreboards-row ${compareFrom ? 'compare-mode' : ''}`}>
        {compareFrom && (
          <div className="compare-instructions">
            Valj en spelare i motstandarlaget att jamfora mot
            <button className="compare-cancel" onClick={() => setCompareFrom(null)}>{t('matchDetail.cancel')}</button>
          </div>
        )}

        <div className={`team-card ${isFinished && currentTeam1Won ? 'winner-card' : ''}`}>
          <div className="team-card-header">
            <span className="team-indicator team1"></span>
            <span className="team-card-name">{match.team1_name}</span>
            {isFinished && currentTeam1Won && <span className="team-card-winner-badge">{t('matchDetail.winner')}</span>}
            <span className="team-card-score">{viewT1Score}</span>
          </div>
          <div className="team-card-columns">
            <span className="col-player">{t('nav.players')}</span>
            <span className="col-stat">K/D/A</span>
            <span className="col-stat">ADR</span>
            <span className="col-stat">HS%</span>
            <span className="col-stat">Rating</span>
            <span className="col-stat">ELO</span>
          </div>
          {viewT1Players.map(player => (
            <TeamPlayerRow key={player.steamid64} player={player} onPlayerClick={onSelectPlayer} team={1} />
          ))}
        </div>

        <div className={`team-card ${isFinished && currentTeam2Won ? 'winner-card' : ''}`}>
          <div className="team-card-header">
            <span className="team-indicator team2"></span>
            <span className="team-card-name">{match.team2_name}</span>
            {isFinished && currentTeam2Won && <span className="team-card-winner-badge">{t('matchDetail.winner')}</span>}
            <span className="team-card-score">{viewT2Score}</span>
          </div>
          <div className="team-card-columns">
            <span className="col-player">{t('nav.players')}</span>
            <span className="col-stat">K/D/A</span>
            <span className="col-stat">ADR</span>
            <span className="col-stat">HS%</span>
            <span className="col-stat">Rating</span>
            <span className="col-stat">ELO</span>
          </div>
          {viewT2Players.map(player => (
            <TeamPlayerRow key={player.steamid64} player={player} onPlayerClick={onSelectPlayer} team={2} />
          ))}
        </div>
      </div>

      {/* === ADVANCED STATS (Spelare / Vapen) === */}
      <div className="weapon-stats-toggle-section">
        <button
          className={`weapon-stats-toggle-btn ${showWeapons ? 'active' : ''}`}
          onClick={() => {
            if (!showWeapons && !weaponStatsData) {
              fetch(`${API_URL}/api/stats/match/${matchid}/weapons`)
                .then(res => res.json())
                .then(data => {
                  if (!data.error && data.players) {
                    setWeaponStatsData(data);
                    const allWeapons = {};
                    data.players.forEach(p => p.weapons.forEach(w => {
                      allWeapons[w.weapon] = (allWeapons[w.weapon] || 0) + w.kills;
                    }));
                    const sorted = Object.entries(allWeapons).sort((a, b) => b[1] - a[1]);
                    if (sorted.length > 0) setSelectedWeapon(sorted[0][0]);
                    if (data.players.length > 0) setSelectedPlayer(data.players[0].steam_id);
                  }
                })
                .catch(() => {});
            }
            if (!showWeapons && !advancedMatchData) {
              // Utility / clutch / economy per player (Fas 2 support tab).
              fetch(`${API_URL}/api/stats/match/${matchid}`)
                .then(res => res.json())
                .then(data => {
                  if (!data.error && data.players) setAdvancedMatchData(data);
                })
                .catch(() => {});
            }
            setShowWeapons(prev => !prev);
          }}
        >
          {showWeapons ? 'Hide Advanced Stats' : 'Advanced Stats'}
        </button>
      </div>

      {showWeapons && weaponStatsData?.players?.length > 0 && (() => {
        const allWeapons = {};
        weaponStatsData.players.forEach(p => p.weapons.forEach(w => {
          const hasData = w.kills > 0 || w.deaths > 0 || w.damage > 0 ||
            w.shots_fired > 0 || w.shots_hit > 0 || w.headshot_kills > 0;
          if (hasData) {
            allWeapons[w.weapon] = (allWeapons[w.weapon] || 0) + w.kills;
          }
        }));
        const sortedWeapons = Object.entries(allWeapons)
          .sort((a, b) => b[1] - a[1]).map(e => e[0]);
        const activeWeapon = selectedWeapon || sortedWeapons[0];
        const allPlayers = [...team1_players, ...team2_players];
        const activePlayerId = selectedPlayer || (weaponStatsData.players[0]?.steam_id);
        const activePlayerData = weaponStatsData.players.find(p => p.steam_id === activePlayerId);
        const activePlayerInfo = allPlayers.find(p => p.steamid64 === activePlayerId);

        const getPlayerWeaponData = (steamId) => {
          const pData = weaponStatsData.players.find(p => p.steam_id === steamId);
          if (!pData) return null;
          return pData.weapons.find(w => w.weapon === activeWeapon);
        };

        return (
          <div className="match-weapon-stats">
            {/* Tab switcher: Spelare / Vapen */}
            <div className="match-advanced-tabs">
              <button
                className={`match-advanced-tab ${advancedTab === 'players' ? 'active' : ''}`}
                onClick={() => setAdvancedTab('players')}
              >
                Players
              </button>
              <button
                className={`match-advanced-tab ${advancedTab === 'weapons' ? 'active' : ''}`}
                onClick={() => setAdvancedTab('weapons')}
              >
                Weapons
              </button>
              {advancedMatchData?.players?.length > 0 && (
                <button
                  className={`match-advanced-tab ${advancedTab === 'support' ? 'active' : ''}`}
                  onClick={() => setAdvancedTab('support')}
                >
                  Support
                </button>
              )}
              {advancedMatchData?.map_control && (
                <button
                  className={`match-advanced-tab ${advancedTab === 'mapcontrol' ? 'active' : ''}`}
                  onClick={() => setAdvancedTab('mapcontrol')}
                >
                  {t('mapControl.tab')}
                </button>
              )}
            </div>

            {/* === MAP CONTROL TAB (awpy 3 steg 3) === */}
            {advancedTab === 'mapcontrol' && advancedMatchData?.map_control && (
              <MapControlChart
                matchId={matchid}
                mapControl={advancedMatchData.map_control}
                activeMap={activeMap}
                playerNames={Object.fromEntries((advancedMatchData.players || []).map((p) => [p.steam_id, p.player_name]))}
              />
            )}

            {/* === WEAPONS TAB === */}
            {advancedTab === 'weapons' && (
              <>
                <div className="match-weapon-tabs">
                  {sortedWeapons.map(wName => (
                    <button
                      key={wName}
                      className={`match-weapon-tab ${activeWeapon === wName ? 'active' : ''}`}
                      onClick={() => setSelectedWeapon(wName)}
                      title={formatWeaponName(wName)}
                    >
                      {getWeaponIconUrl(wName) && (
                        <img
                          src={getWeaponIconUrl(wName)}
                          alt={formatWeaponName(wName)}
                          className="match-weapon-tab-icon"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      )}
                      <span className="match-weapon-tab-name">{formatWeaponName(wName)}</span>
                    </button>
                  ))}
                </div>
                <div className="match-weapon-detail">
                  <div className="match-weapon-detail-header">
                    {getWeaponIconUrl(activeWeapon) && (
                      <img src={getWeaponIconUrl(activeWeapon)} alt="" className="match-weapon-detail-icon" />
                    )}
                    <span className="match-weapon-detail-name">{formatWeaponName(activeWeapon)}</span>
                  </div>
                  <div className="match-weapon-teams">
                    {[
                      { players: team1_players, sids: team1_players.map(p => p.steamid64) },
                      { players: team2_players, sids: team2_players.map(p => p.steamid64) },
                    ].map((team, ti) => {
                      const playersWithData = team.sids.filter(sid => getPlayerWeaponData(sid));
                      if (playersWithData.length === 0) return null;
                      return (
                        <div key={ti} className="match-weapon-team">
                          <div className="match-weapon-team-header">
                            <span className="mw-col-player">{t('nav.players')}</span>
                            <span className="mw-col">Kills</span>
                            <span className="mw-col">Deaths</span>
                            <span className="mw-col">HS%</span>
                            <span className="mw-col">Accuracy</span>
                            <span className="mw-col">DMG</span>
                          </div>
                          {playersWithData.map(sid => {
                            const wd = getPlayerWeaponData(sid);
                            const pInfo = team.players.find(p => p.steamid64 === sid);
                            return (
                              <div key={sid} className="match-weapon-player-row">
                                <span className="mw-col-player">
                                  <SteamAvatar steamid64={sid} size="small" />
                                  <SteamFlag steamid64={sid} className="mw-player-flag" />
                                  <span className="mw-player-name">{pInfo?.name || sid}</span>
                                </span>
                                <span className="mw-col mw-kills">{wd?.kills || 0}</span>
                                <span className="mw-col">{wd?.deaths || 0}</span>
                                <span className="mw-col">{wd?.hs_pct || 0}%</span>
                                <span className="mw-col">{wd?.accuracy || 0}%</span>
                                <span className="mw-col">{wd?.damage || 0}</span>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            )}

            {/* === SUPPORT TAB (utility / flashes / clutch) === */}
            {advancedTab === 'support' && advancedMatchData?.players?.length > 0 && (() => {
              // Aggregate additive demo-stat fields per player across maps.
              const agg = {};
              advancedMatchData.players.forEach(p => {
                const a = agg[p.steam_id] || (agg[p.steam_id] = {
                  utility_damage: 0, flash_assists: 0, enemies_flashed: 0,
                  clutch_wins: 0, clutch_attempts: 0, nades_thrown: 0,
                });
                a.utility_damage += p.utility_damage || 0;
                a.flash_assists += p.flash_assists || 0;
                a.enemies_flashed += p.enemies_flashed || 0;
                a.clutch_wins += p.clutch_wins || 0;
                a.clutch_attempts += p.clutch_attempts || 0;
                a.nades_thrown += p.nades_thrown || 0;
              });

              // Per-team economy + round-context summary. Buy-type rounds are
              // recorded per player, so summing inflates the raw counts ~5x but
              // keeps the win RATIO correct — so we only surface percentages
              // (and avg equip, itself a ratio), never raw round counts.
              const teamSummary = (players) => {
                const sids = new Set(players.map(p => p.steamid64));
                const t = {
                  eqSum: 0, eqRounds: 0,
                  ecoR: 0, ecoW: 0, forceR: 0, forceW: 0,
                  fullR: 0, fullW: 0, pistolR: 0, pistolW: 0,
                  ppR: 0, ppW: 0, rtR: 0, rtW: 0, maR: 0, maW: 0,
                };
                advancedMatchData.players.forEach(p => {
                  if (!sids.has(p.steam_id)) return;
                  t.eqSum += p.equipment_value_sum || 0;
                  t.eqRounds += p.equipment_rounds || 0;
                  t.ecoR += p.eco_rounds || 0; t.ecoW += p.eco_round_wins || 0;
                  t.forceR += p.force_rounds || 0; t.forceW += p.force_round_wins || 0;
                  t.fullR += p.full_buy_rounds || 0; t.fullW += p.full_buy_round_wins || 0;
                  t.pistolR += p.pistol_rounds || 0; t.pistolW += p.pistol_round_wins || 0;
                  t.ppR += p.post_plant_rounds || 0; t.ppW += p.post_plant_round_wins || 0;
                  t.rtR += p.retake_rounds || 0; t.rtW += p.retake_round_wins || 0;
                  t.maR += p.man_adv_rounds || 0; t.maW += p.man_adv_round_wins || 0;
                });
                return t;
              };
              const pct = (w, r) => (r > 0 ? Math.round((w / r) * 100) : null);
              const teams = [
                { name: match.team1_name, sum: teamSummary(team1_players) },
                { name: match.team2_name, sum: teamSummary(team2_players) },
              ];
              const hasEcon = teams.some(tm => tm.sum.eqRounds > 0
                || tm.sum.ppR > 0 || tm.sum.rtR > 0 || tm.sum.maR > 0);

              return (
                <div className="match-weapon-detail">
                  {hasEcon && (
                    <div className="match-team-summary">
                      {teams.map((tm, ti) => {
                        const s = tm.sum;
                        const econRows = [
                          { label: 'Avg Equip', val: s.eqRounds ? `$${Math.round(s.eqSum / s.eqRounds)}` : '—' },
                          { label: 'Pistol Win%', val: pct(s.pistolW, s.pistolR) },
                          { label: 'Eco Win%', val: pct(s.ecoW, s.ecoR) },
                          { label: 'Force Win%', val: pct(s.forceW, s.forceR) },
                          { label: 'Full Buy Win%', val: pct(s.fullW, s.fullR) },
                        ];
                        const ctxRows = [
                          { label: 'Post-Plant Win%', val: pct(s.ppW, s.ppR) },
                          { label: 'Retake Win%', val: pct(s.rtW, s.rtR) },
                          { label: 'Man-Adv Conv%', val: pct(s.maW, s.maR) },
                        ];
                        return (
                          <div key={ti} className="match-team-summary-card">
                            <h4 className="match-team-summary-name">{tm.name}</h4>
                            <div className="match-team-summary-group">
                              <span className="match-team-summary-heading">💰 Economy</span>
                              {econRows.map(r => (
                                <div key={r.label} className="match-team-summary-row">
                                  <span className="mts-label">{r.label}</span>
                                  <span className="mts-val">
                                    {r.val == null ? '—' : (typeof r.val === 'number' ? `${r.val}%` : r.val)}
                                  </span>
                                </div>
                              ))}
                            </div>
                            <div className="match-team-summary-group">
                              <span className="match-team-summary-heading">🧭 Round Context</span>
                              {ctxRows.map(r => (
                                <div key={r.label} className="match-team-summary-row">
                                  <span className="mts-label">{r.label}</span>
                                  <span className="mts-val">{r.val == null ? '—' : `${r.val}%`}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <div className="match-weapon-teams">
                    {[
                      { players: team1_players },
                      { players: team2_players },
                    ].map((team, ti) => {
                      const rows = team.players.filter(p => agg[p.steamid64]);
                      if (rows.length === 0) return null;
                      return (
                        <div key={ti} className="match-weapon-team">
                          <div className="match-weapon-team-header">
                            <span className="mw-col-player">{t('nav.players')}</span>
                            <span className="mw-col">UD</span>
                            <span className="mw-col">Nades</span>
                            <span className="mw-col">FA</span>
                            <span className="mw-col">Enemies Flashed</span>
                            <span className="mw-col">Clutch</span>
                          </div>
                          {rows.map(p => {
                            const a = agg[p.steamid64];
                            return (
                              <div key={p.steamid64} className="match-weapon-player-row">
                                <span className="mw-col-player">
                                  <SteamAvatar steamid64={p.steamid64} size="small" />
                                  <span className="mw-player-name">{p.name || p.steamid64}</span>
                                </span>
                                <span className="mw-col mw-kills">{a.utility_damage}</span>
                                <span className="mw-col">{a.nades_thrown}</span>
                                <span className="mw-col">{a.flash_assists}</span>
                                <span className="mw-col">{a.enemies_flashed}</span>
                                <span className="mw-col">{a.clutch_wins}/{a.clutch_attempts}</span>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* === PLAYERS TAB === */}
            {advancedTab === 'players' && activePlayerData && (
              <div className="match-player-detail">
                <div className="match-player-selector">
                  {allPlayers.map(p => (
                    <button
                      key={p.steamid64}
                      className={`match-player-select-btn ${activePlayerId === p.steamid64 ? 'active' : ''}`}
                      onClick={() => setSelectedPlayer(p.steamid64)}
                    >
                      <SteamAvatar steamid64={p.steamid64} size="small" />
                      <span>{p.name}</span>
                    </button>
                  ))}
                </div>

                <div className="match-player-content">
                  {/* Killed most / Killed most by */}
                  <div className="match-player-duels">
                    {activePlayerData.killed?.length > 0 && (
                      <div className="match-duel-section">
                        <h4 className="match-duel-title">
                          Killed most
                          <span className="match-duel-total">
                            Total kills: {activePlayerData.killed.reduce((s, d) => s + d.kills, 0)}
                          </span>
                        </h4>
                        <div className="match-duel-list">
                          {activePlayerData.killed.slice(0, 5).map(d => (
                            <div key={d.steam_id} className="match-duel-item">
                              <SteamAvatar steamid64={d.steam_id} size="small" />
                              <span className="match-duel-count">{d.kills}x</span>
                              <span className="match-duel-name">{d.name}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {activePlayerData.killed_by?.length > 0 && (
                      <div className="match-duel-section match-duel-deaths">
                        <h4 className="match-duel-title">
                          Killed most by
                          <span className="match-duel-total">
                            Total deaths: {activePlayerData.killed_by.reduce((s, d) => s + d.kills, 0)}
                          </span>
                        </h4>
                        <div className="match-duel-list">
                          {activePlayerData.killed_by.slice(0, 5).map(d => (
                            <div key={d.steam_id} className="match-duel-item">
                              <SteamAvatar steamid64={d.steam_id} size="small" />
                              <span className="match-duel-count">{d.kills}x</span>
                              <span className="match-duel-name">{d.name}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Favorite weapons */}
                  <div className="match-player-weapons">
                    <h4>{t('matchDetail.favoriteWeapons')}</h4>
                    <div className="match-player-weapon-list">
                      <div className="mpw-header">
                        <span className="mpw-col-name">Weapon</span>
                        <span className="mpw-col">Kills</span>
                        <span className="mpw-col">HS%</span>
                        <span className="mpw-col">K/D</span>
                        <span className="mpw-col">Accuracy</span>
                        <span className="mpw-col">DMG</span>
                      </div>
                      {activePlayerData.weapons.slice(0, 8).map(w => {
                        const kd = w.deaths > 0 ? (w.kills / w.deaths).toFixed(2) : w.kills.toFixed(2);
                        return (
                          <div key={w.weapon} className="mpw-row">
                            <span className="mpw-col-name">
                              {getWeaponIconUrl(w.weapon) && (
                                <img
                                  src={getWeaponIconUrl(w.weapon)}
                                  alt=""
                                  className="mpw-icon"
                                  onError={(e) => { e.target.style.display = 'none'; }}
                                />
                              )}
                              {formatWeaponName(w.weapon)}
                            </span>
                            <span className="mpw-col mpw-kills">{w.kills}</span>
                            <span className="mpw-col">{w.hs_pct}%</span>
                            <span className="mpw-col">{kd}</span>
                            <span className="mpw-col">{w.accuracy}%</span>
                            <span className="mpw-col">{w.damage}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* === ALL MATCH HIGHLIGHTS (side by side) === */}
      {(completedHighlights.length > 0 || pendingHighlights.length > 0) && (
        <div className="highlight-section">
          <div className="highlight-section-header">
            <span className="highlight-icon">🎬</span>
            <h3 className="section-heading">{t('matchDetail.matchHighlights')}</h3>
          </div>

          {completedHighlights.length > 0 && (
            <div className="highlights-grid-match">
              {completedHighlights.map(highlight => (
                <div key={highlight.id} className="highlight-item">
                  <div className="highlight-item-header">
                    <span className="highlight-player-info">
                      {highlight.team_name} <span className="separator">·</span> {highlight.player_name}
                    </span>
                    <span className="highlight-type-badge">{formatHighlightType(highlight.highlight_type)}</span>
                  </div>
                  <div className="highlight-video-container">
                    <iframe
                      src={getUniversalEmbedUrl(highlight.video_url)}
                      title={`${highlight.highlight_type} by ${highlight.player_name}`}
                      frameBorder="0"
                      allow="autoplay; encrypted-media"
                      allowFullScreen
                    />
                  </div>
                  {(highlight.ai_comment || canRegenerateComment) && (
                    <div className="highlight-comment-row">
                      {highlight.ai_comment ? (
                        <p className="highlight-card-comment">{highlight.ai_comment}</p>
                      ) : (
                        <p className="highlight-card-comment highlight-card-comment--empty">
                          {t('matchDetail.noAiComment')}
                        </p>
                      )}
                      {canRegenerateComment && (
                        <button
                          type="button"
                          className="highlight-comment-regen-btn"
                          onClick={() => {
                            setCommentInstruction('');
                            setCommentError(null);
                            setCommentTarget(highlight);
                          }}
                          title={t('matchDetail.regenCommentAdmin')}
                          aria-label={t('matchDetail.regenCommentTitle')}
                        >
                          ✨
                        </button>
                      )}
                    </div>
                  )}
                  <HighlightStats highlight={highlight} />
                </div>
              ))}
            </div>
          )}

          {pendingHighlights.length > 0 && (
            <div className="highlights-pending-list">
              {pendingHighlights.map(highlight => (
                <div key={highlight.id} className="highlight-pending-item">
                  <HighlightPreview highlight={highlight} />
                  <div className="render-status">
                    <div className="render-status-icon">⏳</div>
                    <span>Rendering {highlight.team_name}...</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* === COMMENTS === */}
      <MatchComments matchid={matchid} />

      {/* === RANK REVEAL MODAL === */}
      {showRankReveal && newlyRankedPlayer && (
        <RankRevealModal
          elo={newlyRankedPlayer.elo_after}
          rank={newlyRankedPlayer.rank}
          onDismiss={() => setShowRankReveal(false)}
        />
      )}

      {/* === AI RECAP REGENERATE MODAL === */}
      {recapModalOpen && (
        <div
          className="recap-regen-overlay"
          onClick={(e) => { if (e.target === e.currentTarget && !recapRegenerating) setRecapModalOpen(false); }}
        >
          <div className="recap-regen-modal">
            <h3>{t('matchDetail.regenRecapTitle')}</h3>
            <p className="recap-regen-hint">
              {t('matchDetail.recapHint')}
            </p>
            <textarea
              className="recap-regen-textarea"
              value={recapInstruction}
              onChange={(e) => setRecapInstruction(e.target.value)}
              placeholder={t('matchDetail.instructionPlaceholder')}
              rows={4}
              maxLength={500}
              disabled={recapRegenerating}
              autoFocus
            />
            {recapError && <div className="recap-regen-error">⚠️ {recapError}</div>}
            <div className="recap-regen-actions">
              <button
                type="button"
                className="recap-regen-cancel"
                onClick={() => setRecapModalOpen(false)}
                disabled={recapRegenerating}
              >
                Avbryt
              </button>
              <button
                type="button"
                className="recap-regen-submit"
                onClick={regenerateRecap}
                disabled={recapRegenerating}
              >
                {recapRegenerating ? t('matchDetail.generating') : t('matchDetail.regenerate')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* === HIGHLIGHT COMMENT REGENERATE MODAL === */}
      {commentTarget && (
        <div
          className="recap-regen-overlay"
          onClick={(e) => { if (e.target === e.currentTarget && !commentRegenerating) setCommentTarget(null); }}
        >
          <div className="recap-regen-modal">
            <h3>{t('matchDetail.regenCommentTitle')}</h3>
            <p className="recap-regen-hint">
              {commentTarget.player_name} · {formatHighlightType(commentTarget.highlight_type)}.
              {t('matchDetail.commentHint')}
            </p>
            <textarea
              className="recap-regen-textarea"
              value={commentInstruction}
              onChange={(e) => setCommentInstruction(e.target.value)}
              placeholder={t('matchDetail.instructionPlaceholder')}
              rows={3}
              maxLength={500}
              disabled={commentRegenerating}
              autoFocus
            />
            {commentError && <div className="recap-regen-error">⚠️ {commentError}</div>}
            <div className="recap-regen-actions">
              <button
                type="button"
                className="recap-regen-cancel"
                onClick={() => setCommentTarget(null)}
                disabled={commentRegenerating}
              >
                Avbryt
              </button>
              <button
                type="button"
                className="recap-regen-submit"
                onClick={regenerateComment}
                disabled={commentRegenerating}
              >
                {commentRegenerating ? t('matchDetail.generating') : t('matchDetail.regenerate')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MatchDetail;
